import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Clock, User, Pill, FileText, Heart, AlertTriangle, Video,
  Phone, MessageSquare, Calendar, Activity, Home, ChevronDown,
  ChevronUp, Filter, RefreshCw, MapPin, Users
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const UnifiedTimeline = ({ patientId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: 'all',
    dateRange: '7d'
  });
  const [expandedEvents, setExpandedEvents] = useState({});
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  // Event types with icons and colors
  const EVENT_TYPES = {
    visit: { icon: Home, color: 'teal', label: 'Visit' },
    call: { icon: Phone, color: 'blue', label: 'Phone Call' },
    medication: { icon: Pill, color: 'purple', label: 'Medication' },
    symptom: { icon: Activity, color: 'orange', label: 'Symptom' },
    telehealth: { icon: Video, color: 'indigo', label: 'Telehealth' },
    message: { icon: MessageSquare, color: 'green', label: 'Message' },
    alert: { icon: AlertTriangle, color: 'red', label: 'Alert' },
    assessment: { icon: FileText, color: 'amber', label: 'Assessment' },
    vital: { icon: Heart, color: 'rose', label: 'Vital Signs' },
    note: { icon: FileText, color: 'gray', label: 'Note' }
  };

  // Fetch timeline events
  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/api/phase45/timeline/unified`);
      if (patientId) url.searchParams.append('patient_id', patientId);
      url.searchParams.append('range', filters.dateRange);
      if (filters.type !== 'all') url.searchParams.append('type', filters.type);

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Error fetching timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [token, patientId, filters]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  // Toggle event expansion
  const toggleExpand = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  // Get event type info
  const getEventInfo = (type) => EVENT_TYPES[type] || EVENT_TYPES.note;

  // Group events by date
  const groupEventsByDate = (events) => {
    const groups = {};
    events.forEach(event => {
      const date = new Date(event.timestamp).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(event);
    });
    return groups;
  };

  const groupedEvents = groupEventsByDate(events);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Clock className="h-7 w-7 text-teal-600" />
            Unified Care Timeline
          </h1>
          <p className="text-gray-600">Complete chronological view of all care activities</p>
        </div>
        <div className="flex gap-2">
          <select
            value={filters.dateRange}
            onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
            className="border rounded-md px-3 py-2"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <Button variant="outline" onClick={fetchTimeline}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Type Filters */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filters.type === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilters(prev => ({ ...prev, type: 'all' }))}
          className={filters.type === 'all' ? 'bg-teal-600' : ''}
        >
          All Events
        </Button>
        {Object.entries(EVENT_TYPES).map(([type, { icon: Icon, label }]) => (
          <Button
            key={type}
            variant={filters.type === type ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilters(prev => ({ ...prev, type }))}
            className={filters.type === type ? 'bg-teal-600' : ''}
          >
            <Icon className="h-4 w-4 mr-1" />
            {label}
          </Button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

        {/* Events by Date */}
        {Object.entries(groupedEvents).length > 0 ? (
          Object.entries(groupedEvents).map(([date, dateEvents]) => (
            <div key={date} className="mb-8">
              {/* Date Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center z-10">
                  <Calendar className="h-5 w-5 text-gray-600" />
                </div>
                <h3 className="font-semibold text-gray-800">{date}</h3>
                <Badge variant="outline">{dateEvents.length} events</Badge>
              </div>

              {/* Events */}
              <div className="ml-6 space-y-3">
                {dateEvents.map((event, i) => {
                  const eventInfo = getEventInfo(event.type);
                  const Icon = eventInfo.icon;
                  const isExpanded = expandedEvents[event.id];

                  return (
                    <div key={i} className="relative pl-10">
                      {/* Event Icon */}
                      <div 
                        className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                          eventInfo.color === 'teal' ? 'bg-teal-100' :
                          eventInfo.color === 'blue' ? 'bg-blue-100' :
                          eventInfo.color === 'purple' ? 'bg-purple-100' :
                          eventInfo.color === 'orange' ? 'bg-orange-100' :
                          eventInfo.color === 'indigo' ? 'bg-indigo-100' :
                          eventInfo.color === 'green' ? 'bg-green-100' :
                          eventInfo.color === 'red' ? 'bg-red-100' :
                          eventInfo.color === 'amber' ? 'bg-amber-100' :
                          eventInfo.color === 'rose' ? 'bg-rose-100' : 'bg-gray-100'
                        }`}
                      >
                        <Icon className={`h-4 w-4 ${
                          eventInfo.color === 'teal' ? 'text-teal-600' :
                          eventInfo.color === 'blue' ? 'text-blue-600' :
                          eventInfo.color === 'purple' ? 'text-purple-600' :
                          eventInfo.color === 'orange' ? 'text-orange-600' :
                          eventInfo.color === 'indigo' ? 'text-indigo-600' :
                          eventInfo.color === 'green' ? 'text-green-600' :
                          eventInfo.color === 'red' ? 'text-red-600' :
                          eventInfo.color === 'amber' ? 'text-amber-600' :
                          eventInfo.color === 'rose' ? 'text-rose-600' : 'text-gray-600'
                        }`} />
                      </div>

                      {/* Event Card */}
                      <Card 
                        className={`cursor-pointer hover:border-gray-300 transition-colors ${
                          event.priority === 'high' ? 'border-red-200' : ''
                        }`}
                        onClick={() => toggleExpand(event.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs">
                                  {eventInfo.label}
                                </Badge>
                                {event.priority === 'high' && (
                                  <Badge className="bg-red-500 text-xs">High Priority</Badge>
                                )}
                                <span className="text-xs text-gray-500">
                                  {new Date(event.timestamp).toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              <h4 className="font-medium text-gray-800">{event.title}</h4>
                              <p className="text-sm text-gray-600 mt-1">{event.summary}</p>
                              
                              {/* Staff Info */}
                              {event.staff_name && (
                                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                  <User className="h-3 w-3" />
                                  {event.staff_name}
                                  {event.discipline && ` (${event.discipline})`}
                                </div>
                              )}
                            </div>
                            <div className="ml-2">
                              {isExpanded ? (
                                <ChevronUp className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Expanded Details */}
                          {isExpanded && event.details && (
                            <div className="mt-4 pt-4 border-t">
                              <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                {event.details}
                              </div>
                              {event.attachments?.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-gray-500 mb-2">Attachments</p>
                                  <div className="flex gap-2">
                                    {event.attachments.map((att, j) => (
                                      <Badge key={j} variant="outline" className="cursor-pointer hover:bg-gray-100">
                                        <FileText className="h-3 w-3 mr-1" />
                                        {att.name}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Clock className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <h3 className="font-semibold text-gray-600">No Events Found</h3>
              <p className="text-sm text-gray-500">Adjust your filters or date range</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UnifiedTimeline;
